import * as React from 'react'
import clsx from 'clsx'
import { Box, IconButton } from '../../components'
import { TypographyProps, Typography } from '../Typography'
import { DialogContext } from '../Dialog'
import { SvgIconProps } from '../SvgIcon'
import styles from './index.module.scss'
import CloseIcon from '../internal/svg-icons/Close'

export interface DialogTitleClasses {
  /** Styles applied to the root element. */
  root: string
  /** Styles applied to the closeIcon element. */
  closeIcon: string
}

export interface DialogTitleProps extends Omit<TypographyProps, 'classes'> {
  /**
   * Override or extend the styles applied to the component.
   */
  classes?: Partial<DialogTitleClasses>
  /**
   * Override the default close icon element. Shown only if `onClose` is set.
   */
  closeIcon?: React.ReactElement<SvgIconProps>
  /**
   * Callback fired when the close icon is clicked.
   * If set, the close icon will be shown.
   */
  onClose?: React.EventHandler<any>
}

export const DialogTitle = React.forwardRef<HTMLSpanElement, DialogTitleProps>(
  function DialogTitle(props, ref) {
    const {
      classes,
      className,
      id: idProp,
      closeIcon: closeIconProp,
      onClose,
      sx,
      ...other
    } = props

    const { titleId: id = idProp } = React.useContext(DialogContext)

    const handleCloseIconClick = (
      event: React.MouseEventHandler<HTMLOrSVGElement> | unknown
    ) => {
      // Stop the event from bubbling up to the `Chip`
      ;(event as Event).stopPropagation()
      if (onClose) {
        onClose(event)
      }
    }

    let closeIcon = null
    if (onClose) {
      closeIcon =
        closeIconProp && React.isValidElement(closeIconProp) ? (
          React.cloneElement(closeIconProp, {
            //@ts-ignore
            className: clsx(closeIconProp.props.className, classes?.closeIcon),
          })
        ) : (
          <CloseIcon className={clsx(classes?.closeIcon)} />
        )
    }
    if (closeIcon) {
      closeIcon = (
        <IconButton color="secondary" onClick={handleCloseIconClick}>
          {closeIcon}
        </IconButton>
      )
    }

    return (
      <Box
        className={clsx(classes?.root, styles.root, className)}
        sx={{
          display: 'flex',
          justifyContent: 'between',
          alignItems: 'center',
        }}
      >
        <Typography
          component="h2"
          // @ts-ignore
          ref={ref}
          variant="h6"
          weight="demi"
          gutterBottom={false}
          sx={{ ...sx }}
          id={id}
          {...other}
        />
        {closeIcon}
      </Box>
    )
  }
)
